<script setup>
import {
  computed,
  onActivated,
  onMounted,
  onDeactivated,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import GowaAPI from 'dashboard/api/gowa';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  managedInboxId: { type: String, default: null },
  embedded: { type: Boolean, default: false },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const defaults = {
  name: '',
  phone_number: '',
  ignore_groups: false,
  import_history: false,
  sign_agent: false,
};
const form = reactive({ ...defaults });
const options = ['ignore_groups', 'import_history', 'sign_agent'];
const initialInboxId = props.managedInboxId || route.query.inbox_id;
const state = ref(initialInboxId ? 'loading' : 'disconnected');
const error = ref('');
const busy = ref(false);
const loaded = ref(!initialInboxId);
const inboxId = ref(initialInboxId || null);
const existingInbox = ref(Boolean(initialInboxId));
const hasSession = ref(false);
const qr = ref('');
const pairCode = ref('');
const pairingMethod = ref('qr');
const codeInterrupted = ref(false);
const expiresAt = ref(0);
const remaining = ref(0);
const importState = ref('not_requested');
const step = ref('details');
const submitted = ref(false);
const qrExpired = ref(false);
let poll;
let clock;
let disposed = false;
const reading = ref(false);
const connected = computed(() => state.value === 'connected');
const canPair = computed(() => !connected.value && !hasSession.value);
const connectionStatus = computed(() => {
  if (error.value === 'GOWA_UNAVAILABLE') return 'unavailable';
  if (connected.value) return 'connected';
  if (state.value === 'number_mismatch') return 'number_mismatch';
  if (state.value === 'connecting') return 'connecting';
  if (!loaded.value) return 'loading';
  return hasSession.value ? 'disconnected' : 'pairing_required';
});
const connectionStatusClass = computed(() => {
  if (connectionStatus.value === 'connected') {
    return 'bg-n-brand/10 text-n-brand-text';
  }
  if (['unavailable', 'number_mismatch'].includes(connectionStatus.value)) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  if (
    ['connecting', 'disconnected', 'pairing_required'].includes(
      connectionStatus.value
    )
  ) {
    return 'bg-n-amber-3 text-n-amber-12 dark:text-n-amber-11';
  }
  return 'bg-n-alpha-2 text-n-slate-11';
});
const codeMode = computed(() => pairingMethod.value === 'code');
const nameInvalid = computed(() => submitted.value && !form.name.trim());
const phoneInvalid = computed(
  () => submitted.value && !/^\+[1-9][0-9]{7,14}$/.test(form.phone_number)
);
const errorMessage = computed(() => {
  const key = `GOWA.ERRORS.${error.value}`;
  return error.value ? t(key) : '';
});

function showError(err) {
  const code = err.response?.data?.code;
  const known = [
    'INVALID_CONFIGURATION',
    'CONFIGURATION_REQUIRED',
    'NUMBER_MISMATCH',
    'NUMBER_IN_USE',
    'GOWA_CONFLICT',
    'INVALID_PAIRING_METHOD',
    'SESSION_REQUIRED',
    'SESSION_EXISTS',
  ];
  error.value = known.includes(code) ? code : 'GOWA_UNAVAILABLE';
}

function clearPairing() {
  qr.value = '';
  pairCode.value = '';
  qrExpired.value = false;
  codeInterrupted.value = false;
  expiresAt.value = 0;
  remaining.value = 0;
}

function applyConnection(data) {
  state.value = data.state;
  hasSession.value = data.has_session === true;
  importState.value = data.import_state;
}

async function refresh() {
  if (!inboxId.value || reading.value || busy.value || disposed) return;
  const requestedInbox = inboxId.value;
  reading.value = true;
  try {
    const { data } = await GowaAPI.show(requestedInbox);
    if (disposed || busy.value || requestedInbox !== inboxId.value) return;
    if (!loaded.value) {
      Object.keys(form).forEach(key => {
        form[key] = data[key] ?? form[key];
      });
      loaded.value = true;
    }
    applyConnection(data);
    if (
      error.value === 'GOWA_UNAVAILABLE' &&
      (step.value === 'details' ||
        qr.value ||
        pairCode.value ||
        connected.value)
    ) {
      error.value = '';
    }
    if (connected.value || data.state === 'number_mismatch') {
      clearPairing();
    } else if (pairCode.value && data.state === 'disconnected') {
      pairCode.value = '';
      codeInterrupted.value = true;
    }
    if (data.state === 'number_mismatch') error.value = 'NUMBER_MISMATCH';
  } catch (err) {
    if (!disposed) showError(err);
  } finally {
    reading.value = false;
  }
}

async function rememberInbox(id) {
  inboxId.value = String(id);
  await router.replace({ query: { ...route.query, inbox_id: inboxId.value } });
}

async function saveOptions() {
  const creating = !inboxId.value;
  try {
    const { data } = creating
      ? await GowaAPI.create({ ...form })
      : await GowaAPI.configure(inboxId.value, { ...form });
    if (disposed) return;
    applyConnection(data);
    Object.keys(form).forEach(key => {
      form[key] = data[key];
    });
    if (creating) await rememberInbox(data.inbox_id);
    await store.dispatch('inboxes/get');
  } catch (err) {
    // Resume a partially provisioned inbox instead of creating another one.
    if (!disposed && creating && err.response?.data?.inbox_id) {
      await rememberInbox(err.response.data.inbox_id);
    }
    throw err;
  }
}

function validate() {
  submitted.value = true;
  return !nameInvalid.value && !phoneInvalid.value;
}

function returnToDetails() {
  clearPairing();
  step.value = 'details';
}

async function requestPairing() {
  clearPairing();
  const { data } = await GowaAPI.pair(inboxId.value, pairingMethod.value);
  if (disposed) return;
  state.value = data.state;
  qr.value = data.qr_image || '';
  pairCode.value = data.pair_code || '';
  expiresAt.value = Date.parse(data.expires_at || '') || 0;
  remaining.value = Math.max(
    0,
    Math.ceil((expiresAt.value - Date.now()) / 1000)
  );
}

async function submit(method = 'qr') {
  if (busy.value || !loaded.value || !validate()) return;
  busy.value = true;
  error.value = '';
  try {
    await saveOptions();
    if (disposed) return;
    if (existingInbox.value) {
      useAlert(t('GOWA.CONNECTION.SAVED'));
      return;
    }
    if (canPair.value) {
      pairingMethod.value = method;
      step.value = 'pairing';
      await requestPairing();
    }
  } catch (err) {
    if (!disposed) showError(err);
  } finally {
    busy.value = false;
  }
}

async function reconnect(method = 'qr') {
  if (busy.value || !loaded.value || connected.value) return;
  busy.value = true;
  error.value = '';
  try {
    if (hasSession.value) {
      const { data } = await GowaAPI.reconnect(inboxId.value);
      if (!disposed) applyConnection(data);
    } else {
      pairingMethod.value = method;
      step.value = 'pairing';
      await requestPairing();
    }
  } catch (err) {
    if (!disposed) showError(err);
  } finally {
    busy.value = false;
  }
}

async function refreshPairing() {
  if (busy.value || connected.value) return;
  busy.value = true;
  error.value = '';
  try {
    await requestPairing();
  } catch (err) {
    if (!disposed) showError(err);
  } finally {
    busy.value = false;
  }
}

function openAgents() {
  if (props.embedded) {
    returnToDetails();
    refresh();
    return;
  }
  if (existingInbox.value) {
    router.replace({
      name: 'settings_inbox_show',
      params: { inboxId: inboxId.value },
    });
    return;
  }
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { inbox_id: inboxId.value },
    query: { provider: 'gowa' },
  });
}

async function continueToAgents() {
  if (busy.value || !loaded.value || !validate()) return;
  busy.value = true;
  error.value = '';
  try {
    await saveOptions();
    if (!connected.value || disposed) return;
    openAgents();
  } catch (err) {
    if (!disposed) showError(err);
  } finally {
    busy.value = false;
  }
}

function startPolling() {
  if (poll) return;
  disposed = false;
  refresh();
  poll = setInterval(refresh, 4000);
  clock = setInterval(() => {
    if (!qr.value) return;
    remaining.value = Math.max(
      0,
      Math.ceil((expiresAt.value - Date.now()) / 1000)
    );
    if (!remaining.value) {
      qr.value = '';
      qrExpired.value = true;
    }
  }, 1000);
}
onMounted(startPolling);
onActivated(startPolling);

watch(
  () => props.managedInboxId || route.query.inbox_id,
  id => {
    if (String(id || '') === String(inboxId.value || '')) return;
    inboxId.value = id || null;
    existingInbox.value = Boolean(id);
    hasSession.value = false;
    Object.assign(form, defaults);
    loaded.value = !id;
    state.value = id ? 'loading' : 'disconnected';
    step.value = 'details';
    error.value = '';
    submitted.value = false;
    clearPairing();
    pairingMethod.value = 'qr';
    importState.value = 'not_requested';
    refresh();
  }
);
function stopPolling() {
  disposed = true;
  clearInterval(poll);
  poll = null;
  loaded.value = !inboxId.value;
  clearInterval(clock);
  returnToDetails();
}
onDeactivated(stopPolling);
onBeforeUnmount(stopPolling);
</script>

<template>
  <form
    v-if="step === 'details'"
    class="w-full"
    :class="{
      'mx-auto max-w-3xl px-6 py-6 min-h-0 !overflow-y-auto':
        existingInbox && !embedded,
    }"
    novalidate
    @submit.prevent="submit('qr')"
  >
    <h1 v-if="!embedded" class="mb-2 text-lg font-medium text-n-slate-12">
      {{ existingInbox ? t('GOWA.CONNECTION.TITLE') : t('GOWA.TITLE') }}
    </h1>
    <p v-if="!embedded" class="mb-6 text-sm leading-relaxed text-n-slate-11">
      {{
        existingInbox ? t('GOWA.CONNECTION.DESCRIPTION') : t('GOWA.DESCRIPTION')
      }}
    </p>
    <section
      v-if="existingInbox"
      class="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-y border-n-weak py-3"
      aria-live="polite"
    >
      <div class="flex flex-wrap items-center gap-3">
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_CONNECTION.STATUS') }}
        </p>
        <span
          class="rounded-full px-3 py-1 text-xs font-medium"
          :class="connectionStatusClass"
        >
          {{ t(`GOWA.CONNECTION.STATES.${connectionStatus}`) }}
        </span>
      </div>
      <p
        v-if="loaded && !connected"
        class="order-last m-0 basis-full text-sm leading-relaxed text-n-slate-11"
      >
        {{
          t(
            hasSession
              ? 'GOWA.CONNECTION.RECONNECT_HELP'
              : 'GOWA.CONNECTION.PAIR_HELP'
          )
        }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          v-if="!connected"
          type="button"
          icon="i-lucide-refresh-cw"
          :label="t(hasSession ? 'GOWA.CONNECTION.RECONNECT' : 'GOWA.GENERATE')"
          :is-loading="busy"
          :disabled="
            !loaded ||
            busy ||
            (hasSession && state === 'connecting') ||
            state === 'number_mismatch'
          "
          @click="reconnect()"
        />
        <Button
          v-if="canPair"
          type="button"
          variant="outline"
          color="slate"
          :label="t('GOWA.CODE.GENERATE')"
          :disabled="!loaded || busy || state === 'number_mismatch'"
          @click="reconnect('code')"
        />
        <Button
          type="button"
          variant="outline"
          color="slate"
          :label="t('GOWA.CONNECTION.REFRESH')"
          :disabled="busy || reading"
          @click="refresh"
        />
      </div>
    </section>
    <fieldset :disabled="!loaded || busy" class="flex flex-col">
      <div :class="nameInvalid ? 'mb-4' : 'mb-6'">
        <label
          for="gowa-inbox-name"
          class="text-sm font-normal leading-7 text-n-slate-12"
        >
          {{ t('GOWA.NAME') }}
        </label>
        <input
          id="gowa-inbox-name"
          v-model="form.name"
          type="text"
          required
          maxlength="80"
          :aria-invalid="nameInvalid"
          :aria-describedby="nameInvalid ? 'gowa-name-error' : undefined"
          :placeholder="t('GOWA.NAME_PLACEHOLDER')"
          class="w-full !mb-0"
        />
        <p
          v-if="nameInvalid"
          id="gowa-name-error"
          role="alert"
          class="mt-6 mb-0 text-xs text-n-ruby-11"
        >
          {{ t('GOWA.VALIDATION.NAME_REQUIRED') }}
        </p>
      </div>
      <div class="mb-3">
        <label
          for="gowa-phone"
          class="text-sm font-normal leading-7 text-n-slate-12"
        >
          {{ t('GOWA.PHONE') }}
        </label>
        <input
          id="gowa-phone"
          v-model="form.phone_number"
          type="tel"
          required
          pattern="\+[1-9][0-9]{7,14}"
          :aria-invalid="phoneInvalid"
          :aria-describedby="
            phoneInvalid
              ? 'gowa-phone-error gowa-phone-help'
              : 'gowa-phone-help'
          "
          :placeholder="t('GOWA.PHONE_PLACEHOLDER')"
          :readonly="existingInbox || connected"
          class="w-full !mb-6"
        />
        <p
          v-if="phoneInvalid"
          id="gowa-phone-error"
          role="alert"
          class="mb-1 text-xs text-n-ruby-11"
        >
          {{ t('GOWA.VALIDATION.PHONE_INVALID') }}
        </p>
        <p id="gowa-phone-help" class="text-xs text-n-slate-11">
          {{
            t(existingInbox ? 'GOWA.CONNECTION.PHONE_HELP' : 'GOWA.PHONE_HELP')
          }}
        </p>
      </div>
      <div class="flex flex-col gap-4">
        <div v-for="option in options" :key="option">
          <label
            class="flex items-center gap-2 text-sm font-normal text-n-slate-12"
          >
            <Checkbox
              v-model="form[option]"
              :disabled="!loaded || busy"
              class="shrink-0 [&_input]:focus-visible:outline [&_input]:focus-visible:outline-2 [&_input]:focus-visible:outline-offset-2 [&_input]:focus-visible:outline-n-blue-9 dark:[&_input]:focus-visible:outline-n-blue-11"
            />
            {{ t(`GOWA.OPTIONS.${option}.LABEL`) }}
          </label>
          <p class="mt-1 text-xs leading-relaxed text-n-slate-11">
            {{ t(`GOWA.OPTIONS.${option}.HELP`) }}
          </p>
        </div>
      </div>
    </fieldset>
    <p v-if="errorMessage" role="alert" class="mt-5 text-sm text-n-ruby-11">
      {{ errorMessage }}
    </p>
    <p
      v-if="connected && !existingInbox"
      role="status"
      class="mt-6 text-sm text-n-brand-text"
    >
      {{ t(existingInbox ? 'GOWA.CONNECTION.CONNECTED' : 'GOWA.CONNECTED') }}
    </p>
    <p
      v-if="form.import_history && importState !== 'not_requested'"
      role="status"
      class="mt-4 text-xs text-n-slate-11"
    >
      {{ t(`GOWA.IMPORT.${importState}`) }}
    </p>
    <div class="mt-6 flex flex-wrap justify-end gap-3">
      <Button
        type="submit"
        :label="
          t(
            existingInbox || connected || hasSession
              ? 'GOWA.SAVE'
              : 'GOWA.GENERATE'
          )
        "
        :is-loading="busy"
        :disabled="!loaded || busy"
      />
      <Button
        v-if="canPair && !existingInbox"
        type="button"
        variant="outline"
        color="slate"
        :label="t('GOWA.CODE.GENERATE')"
        :disabled="!loaded || busy"
        @click="submit('code')"
      />
      <Button
        v-if="(connected || existingInbox) && !embedded"
        type="button"
        :label="t(existingInbox ? 'GOWA.CONNECTION.BACK' : 'GOWA.CONTINUE')"
        :disabled="busy"
        @click="existingInbox ? openAgents() : continueToAgents()"
      />
    </div>
  </form>
  <section
    v-else
    class="w-full"
    :class="{
      'mx-auto max-w-3xl px-6 py-6 min-h-0 !overflow-y-auto':
        existingInbox && !embedded,
    }"
    aria-labelledby="gowa-pairing-title"
  >
    <h1
      id="gowa-pairing-title"
      class="mb-2 text-lg font-medium text-n-slate-12"
    >
      {{ t(codeMode ? 'GOWA.CODE.TITLE' : 'GOWA.QR_TITLE') }}
    </h1>
    <p class="mb-6 text-sm leading-relaxed text-n-slate-11">
      {{ t(codeMode ? 'GOWA.CODE.DESCRIPTION' : 'GOWA.QR_DESCRIPTION') }}
    </p>
    <p v-if="existingInbox" class="mb-6 text-sm text-n-slate-11">
      {{ t('GOWA.CONNECTION.PAIRING_INBOX', { id: inboxId }) }}
    </p>

    <div class="flex flex-col items-center">
      <template v-if="!connected">
        <template v-if="codeMode">
          <p class="mb-5 text-sm font-medium text-n-slate-12">
            {{ form.phone_number }}
          </p>
          <div
            v-if="pairCode"
            class="w-full max-w-md rounded-xl border border-n-weak bg-n-solid-1 px-3 py-7 text-center font-mono text-2xl tracking-widest text-n-slate-12 sm:text-4xl"
            :aria-label="t('GOWA.CODE.VALUE', { code: pairCode })"
          >
            {{ pairCode }}
          </div>
        </template>
        <div
          v-if="!codeMode && qr"
          class="flex aspect-square w-full max-w-[306px] items-center justify-center rounded-lg border border-n-weak bg-white p-6"
        >
          <img
            :src="qr"
            :alt="t('GOWA.QR_ALT')"
            class="size-full object-contain"
          />
        </div>
        <div
          class="mt-6 w-full max-w-md rounded-lg bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
        >
          <h2 class="mb-3 text-sm font-normal text-n-slate-12">
            {{ t(codeMode ? 'GOWA.CODE.HOW_TO' : 'GOWA.SCAN_TITLE') }}
          </h2>
          <ol
            class="m-0 list-inside list-decimal space-y-1 p-0 text-sm leading-relaxed"
          >
            <template v-if="codeMode">
              <li>{{ t('GOWA.CODE.PHONE_STEP') }}</li>
              <li>{{ t('GOWA.CODE.SETTINGS_STEP') }}</li>
              <li>{{ t('GOWA.CODE.ENTER_STEP') }}</li>
            </template>
            <template v-else>
              <li>{{ t('GOWA.SCAN_PHONE') }}</li>
              <li>{{ t('GOWA.SCAN_SETTINGS') }}</li>
              <li>{{ t('GOWA.SCAN_CAMERA') }}</li>
            </template>
          </ol>
        </div>
      </template>

      <div
        class="mt-6 flex items-center gap-2 text-center text-sm text-n-slate-11"
        role="status"
      >
        <template v-if="busy || qr || pairCode">
          <Spinner :size="12" class="shrink-0 text-n-blue-9" />
          <template v-if="codeMode">
            {{ busy ? t('GOWA.CODE.GENERATING') : t('GOWA.CODE.WAITING') }}
          </template>
          <template v-else>
            {{ busy ? t('GOWA.GENERATING') : t('GOWA.WAITING_SCAN') }}
          </template>
        </template>
        <p v-else-if="connected" class="m-0 text-n-brand-text">
          {{
            t(existingInbox ? 'GOWA.CONNECTION.CONNECTED' : 'GOWA.CONNECTED')
          }}
        </p>
        <p v-else-if="qrExpired" class="m-0">
          {{ t('GOWA.EXPIRED') }}
        </p>
        <p v-else-if="codeInterrupted" class="m-0">
          {{ t('GOWA.CODE.INTERRUPTED') }}
        </p>
      </div>
      <p v-if="errorMessage" role="alert" class="mt-4 text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
      <div class="mt-5 flex flex-wrap justify-center gap-3">
        <Button
          v-if="!connected"
          type="button"
          variant="outline"
          color="slate"
          class="!font-normal !text-n-slate-12 !outline-n-slate-12"
          icon="i-lucide-refresh-cw"
          :label="t(codeMode ? 'GOWA.CODE.REFRESH' : 'GOWA.REFRESH_QR')"
          :disabled="busy || state === 'number_mismatch'"
          @click="refreshPairing"
        />
        <Button
          v-if="codeMode && !connected"
          type="button"
          variant="outline"
          color="slate"
          :label="t('GOWA.CODE.BACK')"
          :disabled="busy"
          @click="returnToDetails"
        />
        <Button
          v-if="!codeMode || connected"
          type="button"
          variant="outline"
          color="slate"
          class="!font-normal !text-n-slate-12 !outline-n-slate-12"
          :label="
            existingInbox
              ? t('GOWA.CONNECTION.BACK')
              : connected
                ? t('GOWA.CONTINUE')
                : t('GOWA.SKIP')
          "
          :disabled="busy || state === 'number_mismatch'"
          @click="openAgents"
        />
      </div>
      <p
        v-if="!connected"
        class="mt-6 max-w-md text-center text-xs leading-relaxed text-n-slate-11"
      >
        {{ t(codeMode ? 'GOWA.CODE.TEMPORARY' : 'GOWA.CONNECT_LATER') }}
      </p>
      <Button
        v-if="errorMessage && !codeMode"
        class="mt-3"
        type="button"
        variant="link"
        color="slate"
        :label="t('GOWA.BACK_TO_DETAILS')"
        :disabled="busy"
        @click="returnToDetails"
      />
    </div>
  </section>
</template>
